import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth, functions } from '../firebase-client';
import { onAuthStateChanged } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../auth/AuthContext';
import ProfileForm from '../components/forms/profile';
import ErrorMessage from '../components/forms/formComponents/ErrorMessage';
import FamilySection from '../components/family/familySection';
import ClaimStandaloneAccountCard from '../components/family/ClaimStandaloneAccountCard';
import EmergencyContactForm from '../components/forms/emergencyContactForm';
import CommunicationPreferencesForm from '../components/forms/communicationPreferences';
import { offlineWriteMessage, useOnlineStatus } from '../utils/onlineStatus';
import { safeInternalReturnPath } from '../utils/externalAttendeeProfile';
import { addChildTargetToReturnPath, CHILD_ACCOUNT_SETUP_REASON } from '../utils/childAccountReturn';
import DestructiveActionDialog from '../components/ui/DestructiveActionDialog';
import DemographicProfileSummary from '../components/profile/DemographicProfileSummary';
const VALID_TABS = new Set(['profile', 'family', 'emergency', 'comms', 'demographics']);
function tabFromSearch(search) {
  const value = new URLSearchParams(search).get('tab');
  return VALID_TABS.has(value) ? value : 'profile';
}
export default function ProfilePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [activeTab, setActiveTab] = useState(() => tabFromSearch(window.location.search));
  const online = useOnlineStatus();
  const params = new URLSearchParams(location.search);
  const returnPath = safeInternalReturnPath(params.get('next'));
  const completingEventTicket = params.get('reason') === 'event-ticket' && Boolean(returnPath);
  const completingChildAccountSetup = params.get('reason') === CHILD_ACCOUNT_SETUP_REASON && Boolean(returnPath);
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, u => {
      if (!u) {
        navigate('/login');
      } else {
        setUser(u);
      }
    });
    return () => unsubscribe();
  }, [navigate]);
  useEffect(() => {
    setActiveTab(tabFromSearch(location.search));
  }, [location.search]);
  const handleTabChange = nextTab => {
    if (!VALID_TABS.has(nextTab)) return;
    setActiveTab(nextTab);
    const params = new URLSearchParams(location.search);
    if (nextTab === 'profile') {
      params.delete('tab');
    } else {
      params.set('tab', nextTab);
    }
    const nextSearch = params.toString();
    navigate(nextSearch ? `/profile?${nextSearch}` : '/profile', {
      replace: true
    });
  };
  const handleDelete = () => {
    if (!online) {
      setError(offlineWriteMessage('delete your account'));
      return;
    }
    setDeleteAccountOpen(true);
  };
  const confirmDeleteAccount = async () => {
    setError(null);
    setDeletingAccount(true);
    try {
      const fn = httpsCallable(functions, 'deleteMyAccount');
      await fn(); // no args
      await signOut();
      navigate('/');
    } catch (e) {
      // If sole-admin guard triggers, you’ll get details in e.customData.blockingTeams
      setError(e.message || 'Failed to delete account.');
      setDeletingAccount(false);
    }
  };
  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (e) {
      setError(e.message);
    }
  };
  const handleChildCreated = child => {
    if (!completingChildAccountSetup) return;
    const nextPath = addChildTargetToReturnPath(returnPath, child?.childUid);
    if (nextPath) navigate(nextPath, { replace: true });
  };
  if (!user) return null;
  return <div className="page">
      <section className="section">
        <div className="card" style={{
        maxWidth: 700,
        marginInline: 'auto'
        }}>
          {completingEventTicket && <div className="card" style={{ marginBottom: '1rem', padding: '1rem', borderColor: '#93c5fd', background: '#eff6ff' }}>
              <h2 style={{ marginTop: 0 }}>Complete your details for checkout</h2>
              <p>Save the requested profile and emergency-contact information, then return to the event to continue buying your ticket.</p>
              <a className="button button-secondary" href={returnPath}>Return to event</a>
            </div>}
          {completingChildAccountSetup && <div className="card" style={{ marginBottom: '1rem', padding: '1rem', borderColor: '#93c5fd', background: '#eff6ff' }}>
              <h2 style={{ marginTop: 0 }}>Set up the child account</h2>
              <p>Create the child account below. We’ll then return you to the course with that child selected.</p>
              <a className="button button-secondary" href={returnPath}>Return to the course</a>
            </div>}
          <div className="tabs">
            <button className={`tab ${activeTab === 'profile' ? 'active' : ''}`} onClick={() => handleTabChange('profile')}>
              My Profile
            </button>
            <button className={`tab ${activeTab === 'family' ? 'active' : ''}`} onClick={() => handleTabChange('family')}>
              Family
            </button>
            <button className={`tab ${activeTab === 'emergency' ? 'active' : ''}`} onClick={() => handleTabChange('emergency')}>
              Emergency Contacts
            </button>
            <button className={`tab ${activeTab === 'comms' ? 'active' : ''}`} onClick={() => handleTabChange('comms')}>
              Communication Preferences
            </button>
            <button className={`tab ${activeTab === 'demographics' ? 'active' : ''}`} onClick={() => handleTabChange('demographics')}>
              Demographics
            </button>
          </div>

          {activeTab === 'profile' && <>
              <h1 className="section-title">My Profile</h1>
              <ProfileForm userId={user.uid} onSave={() => void 0} />
              <ClaimStandaloneAccountCard userId={user.uid} />
              <hr style={{
            margin: '2rem 0'
          }} />
              <button className="button" style={{
            marginRight: '1rem'
          }} onClick={handleLogout}>
                Logout
              </button>
              <button className="button button-danger" onClick={handleDelete} disabled={!online}>
                Delete my account
              </button>
              <ErrorMessage message={error} />
            </>}

          {activeTab === 'family' && <FamilySection user={user} onChildCreated={handleChildCreated} />}

          {activeTab === 'emergency' && <>
              <h1 className="section-title">Emergency Contacts</h1>
              <EmergencyContactForm userId={user.uid} />
            </>}

          {activeTab === 'comms' && <>
              <h1 className="section-title">Communication Preferences</h1>
              <CommunicationPreferencesForm userId={user.uid} />
            </>}

          {activeTab === 'demographics' && <>
              <h1 className="section-title">Private Demographic Profile</h1>
              <DemographicProfileSummary />
            </>}

        </div>
      </section>
      <DestructiveActionDialog
        isOpen={deleteAccountOpen}
        title="Permanently delete your account?"
        message="This removes your Member Nest account and cannot be undone. Club and billing records may need to be retained where legally required."
        confirmationText="DELETE MY ACCOUNT"
        confirmLabel="Delete my account"
        cancelLabel="Keep my account"
        busy={deletingAccount}
        onClose={() => !deletingAccount && setDeleteAccountOpen(false)}
        onConfirm={confirmDeleteAccount}
      />
    </div>;
}
